import { z } from 'zod'

/**
 * Email (design 6.1) and the review workflow: mailbox connections, per-channel email settings,
 * deliverability checks, filtered mail, review actions, the drafting pad and the email journey report.
 */

// ---------------------------------------------------------------------------
// Channel settings (email channels only; stored in channels.config)
// ---------------------------------------------------------------------------
const addr = z.string().trim().toLowerCase().email()
const senderPattern = z.string().trim().toLowerCase().regex(/^([^\s@]+@[^\s@]+\.[^\s@]+|\*?@[^\s@]+\.[^\s@]+)$/, 'Use an address, or @domain for a whole domain')
export const EmailTemplateInput = z.object({
  logo_url: z.string().url().startsWith('https://', 'The logo must be an https:// link').nullable().optional(),
  header_text: z.string().max(120).nullable().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{3,8}$/).nullable().optional(),
  footer_text: z.string().max(1000).nullable().optional(),
  /** This mailbox's own AI notice, replacing the brand's (Brand.ai_footer) on replies in the company's language. */
  ai_disclaimer: z.string().trim().max(1000).nullable().optional(),
  quote_original: z.boolean().default(false),
})
export const EmailChannelSettings = z.object({
  /** The sender's display name on every email from this channel, e.g. "Sam from Example Co" (default: "<assistant> at <company>"). */
  from_name: z.string().trim().max(80).regex(/^[^\r\n<>"]*$/, 'No line breaks, angle brackets or quotes').nullable().optional(),
  /** Where customers' replies go when it is not the sending address (it must be one of the company's email channels). */
  reply_to: addr.nullable().optional(),
  /** Monitoring copies of every outbound email (a monitoring BCC). */
  bcc: z.array(addr).max(5).default([]),
  template: EmailTemplateInput.default({ quote_original: false }),
  /** Senders whose mail is a website form notification (the customer is in the fields). */
  form_senders: z.array(senderPattern).max(50).default([]),
  /** Relays that inline-forward customer mail from one of our own addresses. */
  relay_senders: z.array(senderPattern).max(20).default([]),
  /** More domains that are ours (mail from them is a loop, not a customer). */
  own_domains: z.array(z.string().trim().toLowerCase().regex(/^[a-z0-9.-]+\.[a-z]{2,}$/)).max(20).default([]),
  /**
   * More addresses that deliver to this channel (bookings@, a site's own address): mail to them is this channel's,
   * never dropped or bounced as an unknown address.
   */
  aliases: z.array(addr).max(20).default([]),
  /**
   * May the AI reply on its own to an address read from a website form or a forwarded email? The address is a claim
   * (anyone can type anyone's address), so the default is no: replies to it wait for a person. Even when allowed,
   * the address must appear, whole and exactly, in the email (the verbatim rule).
   */
  claimed_auto_reply: z.boolean().default(false),
  /**
   * Which claims `claimed_auto_reply` covers: form fields; the original sender of mail forwarded by a
   * configured relay; Reply-To on mail from a configured form or relay sender; the sender-reading model's answer.
   */
  claimed_auto_reply_sources: z.array(z.enum(['form', 'forwarded', 'reply_to', 'model'])).max(4).default(['form', 'forwarded', 'reply_to']),
  /** More Authentication-Results servers to trust (your own gateway). */
  trusted_auth_servers: z.array(z.string().trim().toLowerCase()).max(10).default([]),
  /** DKIM selectors to check at setup (Postmark's look like 20240101123456pm). */
  dkim_selectors: z.array(z.string().trim().regex(/^[a-zA-Z0-9._-]+$/)).max(5).default([]),
  /**
   * Read the mailbox's sent mail (Gmail SENT, Microsoft Sent Items): replies people send from the mailbox
   * outside takeit4me become staff replies on the conversation, so shadow drafts are compared with them during
   * a dual run (design 20.1). Never re-sent; our own sends are recognised and skipped.
   */
  read_sent: z.boolean().default(true),
  /**
   * Who may send to the channel's staff copy address (services/staffCopy.ts): the address or @domain the old
   * system sends staff replies from. Copies from anyone else are refused.
   */
  staff_copy_senders: z.array(senderPattern).max(20).default([]),
  /**
   * "Not answered here" (a coverage opt-out): mail to this channel is deliberately not answered on the desk,
   * so the warning that reviewers are closing nearly everything without a reply is not raised for it.
   */
  not_answered_here: z.boolean().default(false),
})
export type EmailChannelSettings = z.infer<typeof EmailChannelSettings>

// ---------------------------------------------------------------------------
// Mailbox connections (OAuth)
// ---------------------------------------------------------------------------
export const MailboxProvider = z.enum(['gmail', 'microsoft'])
export type MailboxProvider = z.infer<typeof MailboxProvider>
export const MailboxConnectStart = z.object({
  provider: MailboxProvider,
  /** Connect an existing email channel; otherwise a new channel is created for the mailbox. */
  channel_id: z.uuid().optional(),
  name: z.string().min(1).max(80).optional(),
  mode: z.enum(['off', 'shadow', 'live']).default('shadow'),
})
/** The private address replies sent from an old system are copied to during a dual run (its monitoring BCC). */
export const StaffCopyInput = z.object({ senders: z.array(senderPattern).min(1).max(20) })
export interface StaffCopyView {
  channel_id: string; address: string | null; senders: string[]
  /** Copies received, matched to a conversation, not matched, and refused (an unknown or failing sender). */
  received: number; matched: number; unmatched: number; refused: number; last_at: string | null
}
export interface MailboxConnectionView {
  id: string
  channel_id: string | null
  provider: MailboxProvider
  account: string
  status: 'active' | 'broken' | 'disconnected'
  last_error: string | null
  /** Gmail watch or Graph subscription expiry, and when it was last renewed. */
  push_expires_at: string | null
  push_renewed_at: string | null
  last_sync_at: string | null
  last_poll_at: string | null
  created_at: string
}

// ---------------------------------------------------------------------------
// DNS: deliverability checks at setup
// ---------------------------------------------------------------------------
export interface DnsCheckItem { name: 'mx' | 'spf' | 'dkim' | 'dmarc'; ok: boolean; status: 'ok' | 'warning' | 'missing' | 'error'; found: string | null; detail: string; fix: string | null }
export interface DnsCheckView { domain: string; provider: string; ok: boolean; checked_at: string; items: DnsCheckItem[] }

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------
export interface AttachmentView {
  id: string
  filename: string
  mime: string
  size: number
  scan_status: 'pending' | 'clean' | 'infected' | 'skipped'
  scan_detail: string | null
  /** Whether the AI read it, and how much text was found. */
  read: boolean
  extracted_chars: number
  downloadable: boolean
}

// ---------------------------------------------------------------------------
// Filtered and ignored mail
// ---------------------------------------------------------------------------
export type FilteredKind = 'filtered' | 'ai_ignored' | 'suspect' | 'failed'
export interface FilteredItemView {
  /** inbound event id (filtered, failed) or message id (ai_ignored, suspect). */
  id: string
  kind: FilteredKind
  at: string
  from: string | null
  subject: string | null
  preview: string
  reason: string
  filter_kind: string | null
  channel: string | null
  conversation_id: string | null
  exception_id: string | null
  rescued: boolean
  /** Failed items: the cause of the failure, the same for every item that failed the same way ("Try again all"). */
  cause?: string | null
  /** Failed items: still being retried automatically (a passing outage), and until when. */
  retrying?: boolean
}
export interface FilteredSummaryView {
  days: { day: string; counts: Record<string, number> }[]
  totals: Record<string, number>
  received: number
  /** Failed and dead intake by cause, for "Try again all". */
  failed_causes?: { cause: string; count: number; transient: boolean; example: string }[]
}
export const RescueInput = z.object({ note: z.string().max(500).optional() })
/** Requeue or dismiss every failed message at once (Requeue all / Dismiss all); confirm must be true. */
export const FailedBulkInput = z.object({ confirm: z.literal(true), note: z.string().max(500).optional() })
export interface FailedBulkResultView { count: number }
/** Failed messages grouped by root cause (ids and numbers collapsed), with a plain-English cause for known ones. */
export interface FailureCauseView { key: string; title: string; count: number; example: string }
export interface FailureCausesView { total: number; causes: FailureCauseView[]; hidden: number }
/** "Try again all" on the Failed tab: every failed or dead email with this cause goes back into intake. */
export const RetryFailedInput = z.object({ cause: z.string().min(1).max(200), note: z.string().max(500).optional() })
export interface RetryFailedResultView { retried: number; cause: string }
/**
 * The customer's address for an email nobody could read the sender of (a rescued form or relayed mail, the
 * "Process it" recovery): a person types it, or accepts the address the AI read.
 */
export const CustomerAddressInput = z.object({
  email: addr,
  name: z.string().trim().max(120).optional(),
  note: z.string().max(500).optional(),
})
export interface CustomerAddressResultView { conversation_id: string; person_id: string; attached_to_existing: boolean }

// ---------------------------------------------------------------------------
// Review workflow
// ---------------------------------------------------------------------------
export const AssignInput = z.object({ staff_id: z.uuid(), note: z.string().max(1000).optional() })
export interface ReviewExtrasView {
  exception_id: string
  /** Hash of the draft as stored now (the console sends the hash of what it showed). */
  draft_hash: string | null
  recipient: { address: string; verified: boolean; how: string } | null
  assignment: { staff_id: string; name: string; by: string | null; note: string | null; at: string; notice: AssignNoticeView[] } | null
  hold: { held_by: string | null; since: string | null; expires_at: string | null } | null
  customer_wrote_again: boolean
  undo: { message_id: string; until: string } | null
  attachments: (AttachmentView & { message_id: string })[]
  /** Colleagues who have this item (or its conversation) open now ("X has this open"). */
  viewing: PresenceView[]
  /** One-key reasons for "No reply needed" (settings.review.no_reply_presets); free text stays possible. */
  no_reply_presets: string[]
}
/** Someone with an item open: claimed on opening, kept alive while they are active, gone three minutes after. */
export interface PresenceView { staff_id: string; name: string; since: string; seen_at: string }
export const PresenceInput = z.object({
  item_kind: z.enum(['exception', 'conversation']),
  item_id: z.uuid(),
  /** false: the person left the item (the claim is removed). */
  open: z.boolean().default(true),
})
/** What actually happened to a reply after it was approved or sent (the reviewer is told the real outcome). */
export interface SendOutcomeView {
  message_id: string
  status: string
  /** sent: the provider took it; waiting: paused or deferred; held: on the desk again; not_sent: suppressed or discarded; failed. */
  outcome: 'pending' | 'sent' | 'waiting' | 'held' | 'not_sent' | 'failed'
  detail: string
  recipient: string | null
  exception_id: string | null
}
/** How the assignee was told: each delivery of the assignment notice (email, push, text) and its state. */
export interface AssignNoticeView { channel: string; status: string; error: string | null; at: string | null }
export interface AssignmentView { exception_id: string; staff_id: string; name: string; by: string | null; note: string | null; at: string }

export const BatchInput = z.object({
  action: z.enum(['approve', 'no_reply_needed']),
  /** expected_hash: the draft the reviewer saw; opened: the reviewer opened this item in full from the batch. */
  items: z.array(z.object({ id: z.uuid(), expected_hash: z.string().optional(), opened: z.boolean().optional() })).min(1).max(50),
  reason: z.string().min(1).max(500).optional(),
  undo_seconds: z.number().int().min(0).max(30).default(0),
}).refine((b) => b.action !== 'no_reply_needed' || !!b.reason, { message: 'Give a reason for closing without a reply', path: ['reason'] })
export interface BatchResultView { done: { id: string; message_id: string | null }[]; skipped: { id: string; reason: string }[] }
export interface BatchSuggestionView {
  key: string
  title: string
  /** Why these can be reviewed as one (the triage model's reason). */
  rationale: string
  reason_code: string
  request_type: string | null
  /** Approve sends the drafts; no_reply closes them. */
  suggested: 'approve' | 'no_reply_needed'
  items: { id: string; ref: string; person: string | null; preview: string; draft_preview: string | null; draft_hash: string | null; recipient_verified: boolean }[]
}
/** The batch suggestions, and how many held items were left out because they need individual judgement. */
export interface BatchSuggestionsView { groups: BatchSuggestionView[]; individual: { id: string; ref: string; why: string }[]; considered: number; triage_error?: string; no_reply_presets: string[] }

export interface UndoResultView { message_id: string; status: string; exception_id: string | null }

export const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'google_review', 'tripadvisor', 'other'] as const
export const DraftPadInput = z.object({
  text: z.string().min(1).max(8000),
  platform: z.enum(SOCIAL_PLATFORMS).default('other'),
  name: z.string().max(120).optional(),
  instruction: z.string().max(1000).optional(),
  /**
   * "Change it" (an iterative social edit): the pad entry whose current reply is edited by `instruction`. The
   * message and platform are the entry's own; only what the instruction asks changes.
   */
  entry_id: z.uuid().optional(),
}).refine((x) => !x.entry_id || Boolean(x.instruction?.trim()), { message: 'Say what to change', path: ['instruction'] })
export interface DraftPadVersionView { reply: string; instruction: string | null; at: string }
export interface DraftPadResultView {
  /** The pad entry this reply belongs to (kept for "Change it" and to reopen later). */
  id: string
  reply: string; checks: { name: string; ok: boolean; detail?: string }[]; citations: { kind: string; id: string; title: string }[]; confidence: number; request_type: string | null; notes: string[]
  /** Earlier replies in this entry, oldest first, each with the instruction that replaced it. */
  versions: DraftPadVersionView[]
}
export interface DraftPadEntryView { id: string; platform: string; name: string | null; message: string; reply: string; versions: DraftPadVersionView[]; created_at: string; updated_at: string }

export const PromiseMarkInput = z.object({ outcome: z.enum(['kept', 'cancelled']), note: z.string().min(1).max(500) })
export interface PromiseListItemView {
  id: string; text: string; kind: string; due_at: string; status: 'open' | 'kept' | 'broken' | 'cancelled'
  made_by: string; made_by_name: string | null; conversation_id: string | null; conversation_ref: string | null; person: string | null; created_at: string; closed_at: string | null
  /** The reply's own words that made it, how to keep it, and what happens if it is not kept. */
  key_phrase: string | null; instructions: string | null; consequence: string | null
  /** The reply gave no time: due_at is the company's default (settings.commitments.untimed_staffed_hours). */
  due_estimated: boolean
}
/** A promise a person records by hand (a commitment the check missed, or one made on a call). */
export const PromiseCreateInput = z.object({
  conversation_id: z.string().uuid(),
  text: z.string().trim().min(3).max(300),
  kind: z.enum(['callback', 'send_item', 'refund', 'follow_up', 'other']).default('other'),
  due_at: z.string().datetime({ offset: true }),
  instructions: z.string().trim().max(500).optional(),
})
export const CommitmentSettingsInput = z.object({
  untimed_staffed_hours: z.number().int().min(1).max(80),
  notify: z.array(z.string().trim().toLowerCase().email()).max(10),
  email_monitoring: z.boolean(),
})

// ---------------------------------------------------------------------------
// The email journey report (journey dashboard)
// ---------------------------------------------------------------------------
export type Granularity = 'hour' | 'day' | 'week'
/** What became of one received email, in order of precedence. */
export type JourneyOutcome = 'spam' | 'not_for_reply' | 'ignored' | 'auto_replied' | 'in_review' | 'sent' | 'escalated' | 'errors'
export const JOURNEY_OUTCOMES: JourneyOutcome[] = ['spam', 'not_for_reply', 'ignored', 'auto_replied', 'in_review', 'sent', 'escalated', 'errors']
export interface EmailReportCard {
  key: string; label: string
  /** A count, or a rate in percent when `unit` is 'pct' (null when there is nothing to divide by). */
  value: number | null; unit?: 'count' | 'pct'
  pct?: number | null
  /** A second line under the figure, such as "41 of 52 sent (AI + people)". */
  sub?: string
  definition: string
}
/** One step of an email's journey: how it went and how long it took. */
export interface JourneyStage { stage: string; label: string; status: 'ok' | 'error' | 'skipped' | 'waiting'; ms: number | null; detail?: string }
export interface EmailReportView {
  from: string; to: string; granularity: Granularity
  /** The company's time zone: hours, days and weeks are cut in it. */
  timezone: string
  /** Every email channel, and the ones this report covers (all when none were chosen). */
  channels: { id: string; name: string; address: string }[]; selected: string[]
  cards: EmailReportCard[]
  counts: Record<JourneyOutcome, number> & { total: number; reached_person: number; classified: number; passed_on: number; commitments: number }
  edits: {
    zero: number; one: number; two_plus: number; written_by_person: number
    /** Reviewed AI drafts sent with at most one change, of reviewed AI drafts sent (percent). */
    at_most_one_rate: number | null
    /** The headline: (auto-replied + 0 edits + 1 edit) of (auto-replied + everything a person sent). */
    le1: { count: number; total: number; rate: number | null }
  }
  funnel: { key: string; label: string; value: number }[]
  /** End-to-end waits, in seconds. */
  latency: { key: string; label: string; median_s: number | null; p90_s: number | null; n: number }[]
  /** Average time per pipeline stage, in milliseconds, with how often it ran and failed. */
  stages: { key: string; label: string; avg_ms: number | null; p90_ms: number | null; n: number; failed: number }[]
  classifications: { key: string; label: string; fired: number }[]
  series: ({ at: string; total: number; commitments: number } & Record<JourneyOutcome, number>)[]
  /** The latest 25 received emails, filtered and failed ones included. */
  recent: {
    id: string; kind: 'message' | 'event'; message_id: string | null; event_id: string | null
    at: string; from: string | null; subject: string | null; conversation_id: string | null
    outcome: JourneyOutcome; outcome_label: string; has_commitments: boolean; stages: JourneyStage[]
  }[]
}
export interface MessageTraceView {
  message: { id: string; at: string; from: string | null; subject: string | null; channel: string; conversation_id: string | null; kind?: 'message' | 'event' }
  /** `ms`: time since the step before. */
  steps: { at: string; stage: string; detail: string; ok: boolean | null; ms?: number | null }[]
  /** Turn failures the AI retried after, and how many attempts the turn took. */
  turn?: { attempts: number; last_error: string | null }
}

/**
 * The draft pin: a short hash of the text a reviewer saw. Same algorithm as core `contentHash`
 * (SHA-256 of the whitespace-normalised text, first 12 bytes in hex), so the console can hash what it shows.
 */
export async function draftHash(text: string): Promise<string> {
  // Web Crypto is global in browsers, Workers and Node; this package has no DOM types, hence the cast.
  const g = globalThis as any
  const buf: ArrayBuffer = await g.crypto.subtle.digest('SHA-256', new g.TextEncoder().encode(text.replace(/\s+/g, ' ').trim()))
  return [...new Uint8Array(buf)].slice(0, 12).map((b) => b.toString(16).padStart(2, '0')).join('')
}
